import { Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy, Input, EventEmitter, ChangeDetectorRef, ViewChild, ViewContainerRef, ComponentRef, Type, OnDestroy, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Form, FormDefinition } from '@nuso/forms/data-access';
import { FormViewComponent } from '../form-view/form-view.component';
import { TranslateService } from '@ngx-translate/core';

export interface FormDialogData {
  formDef: FormDefinition;
  form?: Form;
  title: string;
  input: any;
  saveText?: string;
  cancelText?: string;
  beforeFormHtml?: string;
  afterFormHtml?: string;
  enableSubmit?: boolean;
  allowCancel?: boolean;
  hideButtons?: boolean;

  beforeComponent?: Type<any>;
  afterComponent?: Type<any>;
  beforeComponentData?: any;
  afterComponentData?: any;
}

@Component({
    selector: 'nuso-form-dialog',
    templateUrl: './form-dialog.component.html',
    styleUrls: ['./form-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FormDialogComponent<T = any> implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<FormDialogComponent<T>>>(MatDialogRef);
  private ref = inject(ChangeDetectorRef);
  data = inject<FormDialogData>(MAT_DIALOG_DATA);
  private translateService = inject(TranslateService);


  @Input() busyMessage: string | null;
  @Input() errorMessage: string | null;
  formSubmitted = new EventEmitter<T>();
  formInitiated = new EventEmitter<Form>();

  @ViewChild(FormViewComponent) formView: FormViewComponent;
  @ViewChild('beforeComponentContainer', { read: ViewContainerRef }) beforeContainer: ViewContainerRef;
  @ViewChild('afterComponentContainer', { read: ViewContainerRef }) afterContainer: ViewContainerRef;

  formDef: FormDefinition;
  form: Form | undefined;
  title: string;
  saveText: string;
  cancelText: string;
  afterFormHtml: string | undefined;
  beforeFormHtml: string | undefined;
  input: any;
  enableSubmit = false;
  allowCancel = true;
  hideButtons: boolean;

  beforeComponent: Type<any> | undefined;
  afterComponent: Type<any> | undefined;
  beforeComponentData: any;
  afterComponentData: any;

  private beforeComponentRef: ComponentRef<any>;
  private afterComponentRef: ComponentRef<any>;

  constructor() {
    const data = this.data;

    this.formDef = data.formDef;
    this.form = data.form;
    this.title = data.title;
    this.input = data.input;
    this.afterFormHtml = data.afterFormHtml;
    this.beforeFormHtml = data.beforeFormHtml;
    this.enableSubmit = data.enableSubmit || false;
    this.allowCancel = data.allowCancel ?? true;
    this.saveText = data.saveText || this.translateService.instant('common.save');
    this.cancelText = data.cancelText || this.translateService.instant('common.cancel');
    this.hideButtons = data.hideButtons ?? false;

    this.beforeComponent = data.beforeComponent;
    this.afterComponent = data.afterComponent;
    this.beforeComponentData = data.beforeComponentData;
    this.afterComponentData = data.afterComponentData;
  }

  ngOnInit() {
    setTimeout(() => {
      this.createInjectedComponents();
    });
  }

  ngOnDestroy() {
    if (this.beforeComponentRef) {
      this.beforeComponentRef.destroy();
    }
    if (this.afterComponentRef) {
      this.afterComponentRef.destroy();
    }
  }

  private createInjectedComponents() {
    if (this.beforeComponent && this.beforeContainer) {
      this.beforeComponentRef = this.beforeContainer.createComponent(this.beforeComponent);
      if (this.beforeComponentData) {
        Object.assign(this.beforeComponentRef.instance, this.beforeComponentData);
      }
    }

    if (this.afterComponent && this.afterContainer) {
      this.afterComponentRef = this.afterContainer.createComponent(this.afterComponent);
      if (this.afterComponentData) {
        Object.assign(this.afterComponentRef.instance, this.afterComponentData);
      }
    }

    this.markChanged();
  }

  // Marks this component AND the form view. markForCheck marks ancestors, not
  // descendants, so marking the dialog alone leaves the OnPush form view --
  // and every field in it -- skipped on the next pass. The form controls below
  // it are default change detection, so once the view is checked they follow.
  markChanged() {
    this.ref.markForCheck();
    this.formView?.markChanged();
  }

  onSave(value: T) {
    this.formSubmitted.emit(value);
  }

  onCancel() {
    this.dialogRef.close();
  }

  onFormInit(form: Form) {
    this.formInitiated.emit(form);
  }
}
